import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useConfig } from '../hooks/useConfig';
import apiClient from '../api/client';

export interface ScheduleGroup {
  schedule_group_id: number;
  group_name: string;
  display_label: string;
  description?: string;
  is_active: boolean;
  display_order: number;
}

interface ScheduleGroupContextType {
  scheduleGroups: ScheduleGroup[];
  activeGroupId: number | null;
  defaultGroupId: number | null;
  loading: boolean;
  error: string | null;
  setActiveGroupId: (groupId: number | null) => void;
  setDefaultGroupId: (groupId: number | null) => void;
  refreshScheduleGroups: () => Promise<void>;
  isMultipleSchedulesEnabled: boolean;
}

const ScheduleGroupContext = createContext<ScheduleGroupContextType | undefined>(undefined);

const ACTIVE_GROUP_KEY = 'txjet_active_schedule_group';
const DEFAULT_GROUP_KEY = 'txjet_default_schedule_group';

export const ScheduleGroupProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { config } = useConfig();
  const [scheduleGroups, setScheduleGroups] = useState<ScheduleGroup[]>([]);
  const [activeGroupId, setActiveGroupIdState] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(ACTIVE_GROUP_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [defaultGroupId, setDefaultGroupIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(DEFAULT_GROUP_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMultipleSchedulesEnabled = config?.ALLOW_MULTIPLE_SCHEDULES === 'true';

  // Fetch schedule groups from API
  const refreshScheduleGroups = useCallback(async () => {
    if (!isMultipleSchedulesEnabled) {
      setScheduleGroups([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get<ScheduleGroup[]>('/api/schedule-groups', {
        params: { active_only: 'true' }
      });
      const groups = res.data || [];
      setScheduleGroups(groups.sort((a, b) => a.display_order - b.display_order));

      // If no active group is set, use default or first group
      if (!activeGroupId && groups.length > 0) {
        const groupToUse = defaultGroupId && groups.find(g => g.schedule_group_id === defaultGroupId)
          ? defaultGroupId
          : groups[0].schedule_group_id;
        setActiveGroupIdState(groupToUse);
        sessionStorage.setItem(ACTIVE_GROUP_KEY, groupToUse.toString());
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setScheduleGroups([]);
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch schedule groups');
      setScheduleGroups([]);
    } finally {
      setLoading(false);
    }
  }, [isMultipleSchedulesEnabled, activeGroupId, defaultGroupId]);

  // Set active group ID (persists in sessionStorage)
  const setActiveGroupId = useCallback((groupId: number | null) => {
    setActiveGroupIdState(groupId);
    if (groupId) {
      sessionStorage.setItem(ACTIVE_GROUP_KEY, groupId.toString());
    } else {
      sessionStorage.removeItem(ACTIVE_GROUP_KEY);
    }
  }, []);

  // Set default group ID (persists in localStorage, should be saved to user preferences)
  const setDefaultGroupId = useCallback((groupId: number | null) => {
    setDefaultGroupIdState(groupId);
    if (groupId) {
      localStorage.setItem(DEFAULT_GROUP_KEY, groupId.toString());
      // TODO: Save to user preferences via API
    } else {
      localStorage.removeItem(DEFAULT_GROUP_KEY);
    }
  }, []);

  // Initialize active group from default on login
  useEffect(() => {
    if (user && isMultipleSchedulesEnabled && defaultGroupId && !activeGroupId) {
      setActiveGroupId(defaultGroupId);
    }
  }, [user, defaultGroupId, activeGroupId, isMultipleSchedulesEnabled, setActiveGroupId]);

  // Fetch schedule groups when user logs in and feature is enabled
  useEffect(() => {
    if (user && isMultipleSchedulesEnabled) {
      refreshScheduleGroups();
    } else {
      setScheduleGroups([]);
      setActiveGroupIdState(null);
      sessionStorage.removeItem(ACTIVE_GROUP_KEY);
    }
  }, [user, isMultipleSchedulesEnabled, refreshScheduleGroups]);

  // Clear active group on logout
  useEffect(() => {
    if (!user) {
      setActiveGroupIdState(null);
      sessionStorage.removeItem(ACTIVE_GROUP_KEY);
      setScheduleGroups([]);
    }
  }, [user]);

  return (
    <ScheduleGroupContext.Provider
      value={{
        scheduleGroups,
        activeGroupId,
        defaultGroupId,
        loading,
        error,
        setActiveGroupId,
        setDefaultGroupId,
        refreshScheduleGroups,
        isMultipleSchedulesEnabled,
      }}
    >
      {children}
    </ScheduleGroupContext.Provider>
  );
};

export const useScheduleGroup = () => {
  const context = useContext(ScheduleGroupContext);
  if (context === undefined) {
    throw new Error('useScheduleGroup must be used within a ScheduleGroupProvider');
  }
  return context;
};

